import {
	Fragment,
	type MouseEvent,
	type ReactElement,
	type ReactNode,
	cloneElement,
	memo,
	useState,
} from 'react';

/**
 *	Formatters
 *
 *	A formatter consists of at least a selector and a decorator. The selector is also used to split the link,
 *	so it cannot have any capture groups except for the top most one, e.g; /(hello (?:world))/gi will capture
 *	"hello" + "word", but only split on "hello world".
 *
 *	Formatters can also be defaultIgnored and needs to be explicitly included in allowedFormatters on use.
 *	Formatters that use embeds (video, iframe, oEmbed) *must* set the isEmbed flag to true.
 *
 *	@example
 *
 *	addFormatter('md:anchors', {
 *		selector: /(\[.*?\]\(.*?\))/gi,
 *		decorator: (guid: string, match: string, selector: RegExp): ReactNode => {
 *			match.match(/(\[.*?\]\(.*?\))/gi);
 *			return <a key={guid} href={RegExp.$2}>{RegExp.$1}</a>
 *		}
 *	});
 */

type FormatterDecorator = (
	guid: string,
	match: string,
	meta: {
		subject?: string;
		selector?: RegExp;
		matches?: RegExpMatchArray;
		args?: any[];
	}
) => ReactNode;

interface FormatterDefinition {
	selector: RegExp;
	decorator: FormatterDecorator;
	defaultIgnored?: boolean;
	ignoreMatchSplit?: boolean;
	matchIndex?: number;
	isEmbed?: boolean;
}

const loadedFormatters = new Map<string, FormatterDefinition>();

function isFragment(maybeFragment: unknown) {
	if ((maybeFragment as any)?.type) {
		return (maybeFragment as any)?.type === Fragment;
	}
	return maybeFragment === Fragment;
}

export type FormatterDefinitions = Map<string, FormatterDefinition>;

export function getFormatters(): FormatterDefinitions {
	return loadedFormatters;
}

export function getDefaultFormatters(): FormatterDefinitions {
	const defaultFormatters = new Map<string, FormatterDefinition>();

	for (const [name, formatter] of getFormatters().entries()) {
		if (formatter.defaultIgnored !== true) {
			defaultFormatters.set(name, formatter);
		}
	}

	return defaultFormatters;
}

export function getFormatter(name: string): Nullable<FormatterDefinition> {
	return loadedFormatters.get(name) ?? null;
}

export function addFormatter(name: string, defs: FormatterDefinition): void {
	loadedFormatters.set(name, defs);
}

export function removeFormatter(name: string): void {
	loadedFormatters.delete(name);
}

function filterAllowedFormatters(
	formatters: FormatterDefinitions,
	allowedFormatters: string[]
): FormatterDefinitions {
	const filteredFormatters = new Map<string, FormatterDefinition>();

	if (allowedFormatters.length === 0) {
		return formatters;
	}

	for (const [name, formatter] of formatters.entries()) {
		if (allowedFormatters.includes(name)) {
			filteredFormatters.set(name, formatter);
		}
	}

	// @NOTE Include formatters that are defaultIgnored
	const allFormatters = getFormatters();

	allowedFormatters.forEach((name: string) => {
		if (allFormatters.has(name) && !filteredFormatters.has(name)) {
			filteredFormatters.set(
				name,
				allFormatters.get(name) as FormatterDefinition
			);
		}
	});

	return filteredFormatters;
}

function filterIgnoredFormatters(
	formatters: FormatterDefinitions,
	ignoredFormatters: string[]
): FormatterDefinitions {
	const filteredFormatters = new Map<string, FormatterDefinition>();

	if (ignoredFormatters.length === 0) {
		return formatters;
	}

	for (const [name, formatter] of formatters.entries()) {
		if (ignoredFormatters.includes(name)) {
			filteredFormatters.set(name, formatter);
		}
	}

	return filteredFormatters;
}

function countEmbeds(raw: string, formatters?: FormatterDefinitions): number {
	if (raw.length === 0) return 0;

	if (!formatters) {
		formatters = getDefaultFormatters();
	}

	if (!formatters) return 0;

	let numEmbeds = 0;

	prepareRaw(raw)
		.split(/[\r\n\t]{2,}/gm)
		.filter((n) => n)
		.forEach((item) => {
			for (const [, { selector, isEmbed }] of (
				formatters as FormatterDefinitions
			).entries()) {
				const match = item.match(selector);

				if (match !== null && match.length > 0 && isEmbed) {
					numEmbeds++;
				}
			}
		});

	return numEmbeds;
}

interface BlockEntryProps {
	inline?: boolean;
	className?: string;
	children: ReactNode | ReactNode[];
}

function BlockEntry({
	inline = false,
	className,
	children,
}: BlockEntryProps): ReactNode {
	return inline ? (
		<span className={className}>{children}</span>
	) : (
		<section className={className}>{children}</section>
	);
}

interface FormattedBlockProps {
	raw: string;
	wrapWith?: ReactElement<HTMLElement>;
	allowedFormatters?: string[];
	ignoredFormatters?: string[];
	className?: string;
}

/**
 *	FormattedBlock
 *
 *	Formats a single block (a block is part of a string separated by two line breaks).
 *	Returned element will have data attributes, has-embeds and num-embeds.
 *	FormattedBlock does not handle collapsed content, prefix, suffix nor collapse-triggers.
 *
 * 	@see FormattedContent
 *
 *	@param raw string
 *	@param wrapWith? ReactElement
 *	@param allowedFormatters? string[]
 *	@param ignoredFormatters? string[]
 *
 *	@return ReactNode
 */
export function FormattedBlock({
	raw,
	wrapWith,
	allowedFormatters = [],
	ignoredFormatters = [],
}: FormattedBlockProps): ReactNode {
	let formatters = getDefaultFormatters();

	if (allowedFormatters.length > 0) {
		formatters = filterAllowedFormatters(formatters, allowedFormatters);
	}

	if (ignoredFormatters.length > 0) {
		formatters = filterIgnoredFormatters(formatters, ignoredFormatters);
	}

	let numEmbeds = 0;

	if (!wrapWith) {
		wrapWith = <Fragment />;
	}

	const formatAll = (text: string): ReactNode => {
		let formatted: string | ReactNode = text;

		for (const [
			name,
			{ selector, decorator, ignoreMatchSplit },
		] of formatters.entries()) {
			const matches = selector.exec(text);
			const match = !!text.match(selector);

			if (match) {
				let match;
				let segments: (string | ReactNode)[] = text.split(selector);

				if (ignoreMatchSplit) {
					segments = ['', formatted];
				}

				for (let n = 1, l = segments.length; n < l; n += 2) {
					match = segments[n];

					if (!match) continue;

					const guid: string = [name, n].join(':');

					formatted = decorator(guid, match as string, {
						selector,
						matches: matches as any,
						subject: text,
					});

					segments[n] = formatted;
					numEmbeds++;
				}

				segments = segments.map((item: any, i: number) => {
					if (typeof item === 'string') {
						return <Fragment key={i}>{formatAll(item)}</Fragment>;
					}

					return <Fragment key={i}>{item}</Fragment>;
				});

				return <Fragment>{segments}</Fragment>;
			}
		}

		return <Fragment>{formatted}</Fragment>;
	};

	const formatted = raw
		.split(/[\r\n]{2,}/gm)
		.filter((n) => n)
		.map((item, index) => <Fragment key={index}>{formatAll(item)}</Fragment>);

	let dataAttributes: Record<string, string | number | boolean> = {
		'data-has-embeds': numEmbeds > 0,
		'data-num-embeds': numEmbeds,
	};

	if (isFragment(wrapWith)) {
		dataAttributes = {};
	}

	return cloneElement(
		wrapWith,
		{
			...dataAttributes,
		},
		formatted
	);
}

interface FormattedContentProps {
	raw: string;
	maxLength?: number;
	prefixWith?: ReactElement<HTMLElement>;
	suffixWith?: ReactElement<HTMLElement>;
	expandWith?: ReactElement<HTMLElement>;
	wrapWith?: ReactElement<HTMLElement>;
	wrapBlockWith?: ReactElement<HTMLElement>;
	allowedFormatters?: string[];
	ignoredFormatters?: string[];
	inline?: boolean;
	wrapperClassName?: string;
	blockClassName?: string;
	rawFormatter?: (blocks: string[]) => string[];
}

/**
 *	Prepares raw by prefixing, and suffiuxing line breaks for possible embed links
 *
 *	@param raw string
 *	@returns string
 */
function prepareRaw(raw: string): string {
	const replace = `\n\n$1\n\n`;

	const regexVimeo: RegExp =
		/((https?:\/\/?(?:www.)?(?:vimeo.com)(?:\S+)?))/gim;

	const regexYouTube: RegExp =
		/(((?:https?:)?\/{2})?((?:www|m)\.)?((?:youtube\.com|youtu.be))(\/(?:[\w-]+\?v=|embed\/|v\/)?)([\w-]+)(\S+)?$)/gim;

	return raw.replace(regexVimeo, replace).replace(regexYouTube, replace).trim();
}

/**
 *	FormattedContent
 *
 *	Splits a raw string into one or more blocks and formats them {@see FormattedBlock}.
 *	FormattedContent also handles collapsing of blocks if maxLength is set. Additonally,
 *	a custom wrapper for the whole component, and each block can be set if needed.
 *
 *	Wrapped component will have data attributes;
 *
 *	@attr has-embeds boolean
 *	@attr num-embeds number
 *	@attr num-blocks number
 *	@attr num-visible-blocks number
 *
 *	@param raw string
 *	@param maxLength? number (-1 will not collapse)
 *	@param prefixWith? ReactElement Component will prefix (inside) first block
 *	@param suffixWith? ReactElement Component will suffix (inside) last block
 *	@param expandWith? ReactElement Component used to expand content if collapsed
 *	@param wrapWith? ReactElement
 *	@param wrapBlockWith? ReactElement
 *	@param allowedFormatters? string[]
 *	@param ignoredFormatters? string[]
 *	@param rawFormatter? A function that maps over each split item and prepares them for each formatter
 *
 *	@return ReactNode
 */
export const FormattedContent = memo(function FormattedContent({
	raw,
	maxLength = -1,

	prefixWith,
	suffixWith,
	expandWith,
	wrapWith,
	wrapBlockWith,

	allowedFormatters = [],
	ignoredFormatters = [],
	inline = false,

	wrapperClassName,
	blockClassName,

	rawFormatter,
}: FormattedContentProps): Nullable<ReactNode> {
	const hasDefinedWrapper = wrapBlockWith !== undefined;

	let formatters = getDefaultFormatters();

	if (allowedFormatters.length > 0) {
		formatters = filterAllowedFormatters(formatters, allowedFormatters);
	}

	if (ignoredFormatters.length > 0) {
		formatters = filterIgnoredFormatters(formatters, ignoredFormatters);
	}

	let blocks = prepareRaw(raw).split(/\r?\n{2,}/gm);

	if (rawFormatter) {
		blocks = rawFormatter(blocks).filter((n: string) => n);
	}

	const numBlocks = blocks.length;
	const numEmbeds = countEmbeds(raw, formatters);

	if (!wrapWith) {
		wrapWith = inline ? <span /> : <div />;
	}

	if (!wrapBlockWith) {
		wrapBlockWith = <Fragment />;
	}

	const wrapProps = {
		className: wrapperClassName,
		'data-has-embeds': numEmbeds > 0,
		'data-num-embeds': numEmbeds,
		'data-num-blocks': numBlocks,
		'data-num-visible-blocks': numBlocks,
	};

	const deafaultCollapsed: boolean =
		maxLength > 0 && blocks.join(' ').length > maxLength;

	const [isCollapsed, setCollapsed] = useState<boolean>(deafaultCollapsed);

	if (!numBlocks) return null;

	const handleExpand = (event: MouseEvent) => {
		event.preventDefault();

		setCollapsed(false);
	};

	if (!expandWith) {
		expandWith = (
			<a href="#read-more" onClick={handleExpand}>
				Read more
			</a>
		);
	} else {
		const attrs: Record<string, any> = {
			onClick: handleExpand,
		};

		expandWith = cloneElement(expandWith, attrs);
	}

	let firstBlock = blocks[0];
	let secondBlock = blocks[1] ?? '';

	const firstBlockContainsURL = firstBlock?.match(/(https?:\/\/[^\s]+)/g);

	if (isCollapsed && maxLength > 0 && blocks.join(' ').length > maxLength) {
		const ellipsify = (content: string, limit: number, skipSuffix = false) => {
			let suffix = '';

			const trimmed = content.slice(0, limit).trimRight();

			if (!skipSuffix && content.length > limit) {
				suffix = '…';
			}

			return `${trimmed}${suffix}`;
		};

		firstBlock = ellipsify(firstBlock ?? '', maxLength);

		// Check if first block contains a URL
		if (firstBlockContainsURL && secondBlock.length > 0) {
			firstBlock = blocks[0];
			secondBlock = ellipsify(secondBlock, maxLength);
		}

		// @NOTE Validate first paragraph length, this hould only occur with a really long URL.
		if (firstBlock && firstBlock.length > maxLength) {
			firstBlock = ellipsify(firstBlock, maxLength, !!firstBlockContainsURL);

			// @NOTE Check if second block should be hidden
			if (firstBlock.length >= maxLength && firstBlock.endsWith('…')) {
				secondBlock = '';
			}
		}

		if (
			firstBlock &&
			!firstBlockContainsURL &&
			firstBlock.length < maxLength &&
			!firstBlock.endsWith('…') &&
			secondBlock.length > 0
		) {
			secondBlock = secondBlock = ellipsify(
				secondBlock,
				maxLength - firstBlock.length
			);
		}

		const formattedBlock = (
			<Fragment>
				{secondBlock ? (
					<Fragment>
						<BlockEntry inline={inline} className={blockClassName}>
							{prefixWith}{' '}
							<FormattedBlock
								raw={firstBlock ?? ''}
								wrapWith={wrapBlockWith}
								allowedFormatters={allowedFormatters}
								ignoredFormatters={ignoredFormatters}
							/>
						</BlockEntry>
						<BlockEntry inline={inline} className={blockClassName}>
							<FormattedBlock
								raw={secondBlock}
								wrapWith={wrapBlockWith}
								allowedFormatters={allowedFormatters}
								ignoredFormatters={ignoredFormatters}
							/>{' '}
							{expandWith}
						</BlockEntry>
					</Fragment>
				) : (
					<BlockEntry inline={inline} className={blockClassName}>
						{prefixWith}{' '}
						<FormattedBlock
							raw={firstBlock ?? ''}
							wrapWith={wrapBlockWith}
							allowedFormatters={allowedFormatters}
							ignoredFormatters={ignoredFormatters}
						/>{' '}
						{expandWith}
					</BlockEntry>
				)}
			</Fragment>
		);

		let dataAttributes: Record<string, string | number | boolean> = {
			'data-num-visible-blocks': secondBlock ? 2 : 1,
		};

		if (isFragment(wrapWith)) {
			dataAttributes = {};
		}

		return cloneElement(
			wrapWith,
			{
				...wrapProps,
				...dataAttributes,
			},
			formattedBlock
		);
	}

	const formattedBlocks: ReactElement[] = blocks.map((block, key) => (
		<FormattedBlock
			key={`fmb:${key}`}
			raw={block}
			wrapWith={wrapBlockWith}
			allowedFormatters={allowedFormatters}
			ignoredFormatters={ignoredFormatters}
			className={blockClassName}
		/>
	));

	return cloneElement(
		wrapWith,
		wrapProps,
		<Fragment>
			{formattedBlocks.map((block, key) => {
				const isFirst = key === 0;
				const isLast = key === formattedBlocks.length - 1;

				return (
					<BlockEntry
						inline={inline}
						key={`block:${key}`}
						className={blockClassName}>
						{isFirst && prefixWith && <Fragment>{prefixWith} </Fragment>}
						{block}
						{isLast && suffixWith && <Fragment>{suffixWith} </Fragment>}
					</BlockEntry>
				);
			})}
		</Fragment>
	);
});
